import { useState, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Default services as fallback
const DEFAULT_SERVICES = [
  {
    id: "gartenpflege",
    name: "Gartenpflege",
    shortDesc: "Professionelle Pflege für Ihren Garten das ganze Jahr über",
    description: "Von der Rasenpflege über Heckenschnitt bis zur Beetgestaltung – wir kümmern uns um die komplette Gartenpflege und sorgen dafür, dass Ihr Garten zu jeder Jahreszeit gepflegt aussieht.",
    features: [
      "Rasenmähen und Rasenpflege",
      "Heckenschnitt und Formschnitt",
      "Unkrautbekämpfung",
      "Beetpflege und Bepflanzung",
      "Laubbeseitigung",
      "Düngung und Bewässerung"
    ],
    faqs: [
      { question: "Wie oft sollte der Rasen gemäht werden?", answer: "In der Wachstumsphase von April bis Oktober empfehlen wir eine wöchentliche Mahd." },
      { question: "Wann ist der beste Zeitpunkt für den Heckenschnitt?", answer: "Der Hauptschnitt erfolgt idealerweise im Juni nach der Brutzeit." },
      { question: "Bieten Sie auch regelmäßige Pflegeverträge an?", answer: "Ja, wir bieten individuelle Pflegeverträge an." }
    ]
  },
  {
    id: "baumfaellung",
    name: "Baumfällung & Baumpflege",
    shortDesc: "Sichere Baumfällung und professionelle Baumpflege",
    description: "Ob Problemfällung, Baumschnitt oder Sturmschadenbeseitigung – unsere erfahrenen Baumpfleger führen alle Arbeiten fachgerecht und sicher durch.",
    features: [
      "Fachgerechte Baumfällung",
      "Baumschnitt und Kronenpflege",
      "Sturmschadenbeseitigung",
      "Totholzentfernung",
      "Stubben fräsen",
      "Seilklettertechnik"
    ],
    faqs: [
      { question: "Benötige ich eine Genehmigung für die Baumfällung?", answer: "Für geschützte Bäume ist eine Fällgenehmigung erforderlich. Wir beraten Sie gerne." },
      { question: "Wie lange dauert eine Baumfällung?", answer: "Je nach Größe und Lage zwischen 2-8 Stunden." },
      { question: "Was passiert mit dem gefällten Holz?", answer: "Wir entsorgen es fachgerecht oder schneiden es als Brennholz zu." }
    ]
  },
  {
    id: "baggerarbeiten",
    name: "Baggerarbeiten / Erdarbeiten",
    shortDesc: "Erdarbeiten mit modernem Gerät für Ihr Bauprojekt",
    description: "Mit unserem Minibagger führen wir präzise Erdarbeiten durch – ob für Fundamente, Teiche, Drainage oder Geländemodellierung.",
    features: [
      "Aushub für Fundamente",
      "Teichbau und Teichsanierung",
      "Drainage-Verlegung",
      "Geländemodellierung",
      "Pflasterarbeiten Vorbereitung",
      "Kabelgräben"
    ],
    faqs: [
      { question: "Wie groß ist Ihr Bagger?", answer: "Wir arbeiten mit einem kompakten Minibagger (ca. 1,5m Breite)." },
      { question: "Übernehmen Sie auch die Entsorgung des Aushubs?", answer: "Ja, wir entsorgen fachgerecht oder verteilen auf Wunsch." },
      { question: "Können Sie auch bei engem Zugang arbeiten?", answer: "Ja, unser kompakter Minibagger passt auch in enge Bereiche." }
    ]
  },
  {
    id: "entsorgung",
    name: "Entsorgung",
    shortDesc: "Fachgerechte Entsorgung von Grünschnitt und Gartenabfällen",
    description: "Wir entsorgen Grünschnitt, Gartenabfälle, Erdaushub und andere Materialien fachgerecht und umweltfreundlich.",
    features: [
      "Grünschnittentsorgung",
      "Erdaushub",
      "Kompostierung",
      "Altholz-Entsorgung",
      "Gartenabfälle",
      "Container-Service"
    ],
    faqs: [
      { question: "Wohin bringen Sie den Grünschnitt?", answer: "Zu zertifizierten Kompostieranlagen und Recyclinghöfen." },
      { question: "Können Sie auch größere Mengen entsorgen?", answer: "Ja, wir organisieren bei Bedarf Container." },
      { question: "Was kostet die Entsorgung?", answer: "Die Kosten hängen von Art und Menge ab. Wir erstellen ein Angebot." }
    ]
  },
  {
    id: "hochdruckreinigung",
    name: "Hochdruckreinigung",
    shortDesc: "Professionelle Reinigung von Terrassen, Einfahrten und Fassaden",
    description: "Mit professioneller Hochdrucktechnik reinigen wir Ihre Außenanlagen schonend und gründlich.",
    features: [
      "Terrassenreinigung",
      "Einfahrten und Gehwege",
      "Fassadenreinigung",
      "Steinmauern",
      "Zäune und Gartenmöbel",
      "Moos- und Algenentfernung"
    ],
    faqs: [
      { question: "Beschädigt der Hochdruckreiniger die Oberflächen?", answer: "Nein, wir arbeiten mit angepasstem Druck für jede Oberfläche." },
      { question: "Wie oft sollte man Terrassen reinigen lassen?", answer: "Wir empfehlen 1-2 Mal jährlich, besonders im Frühjahr." },
      { question: "Verwenden Sie Chemikalien?", answer: "In der Regel nur Wasser. Bei Bedarf umweltfreundliche Reinigungsmittel." }
    ]
  }
];

export const useSiteServices = () => {
  const [services, setServices] = useState(DEFAULT_SERVICES);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/service-categories`);
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            // Map API response to expected format
            const mappedServices = data.map(s => ({
              id: s.id,
              name: s.name,
              shortDesc: s.short_desc || s.shortDesc || DEFAULT_SERVICES.find(d => d.id === s.id)?.shortDesc || '',
              description: s.description || DEFAULT_SERVICES.find(d => d.id === s.id)?.description || '',
              features: s.features || DEFAULT_SERVICES.find(d => d.id === s.id)?.features || [],
              faqs: s.faqs || DEFAULT_SERVICES.find(d => d.id === s.id)?.faqs || []
            }));
            setServices(mappedServices);
          }
        }
      } catch (error) {
        console.error('Error fetching services:', error);
        // Keep default services on error
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  const getServiceById = (id) => {
    return services.find(s => s.id === id) || null;
  };

  return { services, loading, getServiceById };
};

export default useSiteServices;
